import { useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, Sparkles, Tag, DollarSign, FileText, CheckCircle, ChevronRight } from 'lucide-react';
import { PhotoCapture, PriceEstimator } from '../features/smart-listing';
import type { DraftListing, PhotoAnalysis } from '../features/smart-listing';

const listingSteps = [
  { id: 'photos', title: 'Photos', icon: Camera },
  { id: 'category', title: 'Category', icon: Tag },
  { id: 'details', title: 'Details', icon: FileText },
  { id: 'pricing', title: 'Pricing', icon: DollarSign },
];

export default function SmartListingDemo(): JSX.Element {
  const [currentStep, setCurrentStep] = useState(0);
  const [draft, setDraft] = useState<Partial<DraftListing>>({
    photos: [],
    category: '',
    title: '',
    description: '',
    price: 0,
    location: '',
  });
  const [isGenerating, setIsGenerating] = useState(false);

  const handlePhotosChange = (photos: PhotoAnalysis[]) => {
    setDraft((prev) => ({ ...prev, photos }));
  };

  const handlePriceSelect = (price: number) => {
    setDraft((prev) => ({ ...prev, price }));
  };

  const generateAIContent = async () => {
    setIsGenerating(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setDraft((prev) => ({
      ...prev,
      title: 'iPhone 14 Pro Max 256GB - Like New Condition',
      description:
        'Selling my iPhone 14 Pro Max in excellent condition. Used for 6 months with screen protector and case. Battery health at 95%. Includes original box, charger, and unused earphones. Deep Purple color. No scratches or dents.',
    }));
    setIsGenerating(false);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return (draft.photos?.length || 0) > 0;
      case 1:
        return draft.category;
      case 2:
        return draft.title && draft.description;
      case 3:
        return draft.price && draft.price > 0;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-dark-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-dark-900 mb-3">
            <Sparkles className="w-8 h-8 inline-block mr-2 text-primary-500" />
            Smart Listing Assistant
          </h1>
          <p className="text-dark-500">
            AI-powered listing creation with photo analysis and market intelligence
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between">
            {listingSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <div key={step.id} className="flex items-center">
                  <motion.div
                    className={`flex flex-col items-center ${
                      isActive
                        ? 'text-primary-600'
                        : isCompleted
                        ? 'text-success'
                        : 'text-dark-300'
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-colors ${
                        isActive
                          ? 'bg-primary-100'
                          : isCompleted
                          ? 'bg-success/10'
                          : 'bg-dark-100'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className="text-xs font-medium">{step.title}</span>
                  </motion.div>
                  {index < listingSteps.length - 1 && (
                    <div
                      className={`w-12 h-0.5 mx-2 ${
                        index < currentStep ? 'bg-success' : 'bg-dark-200'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-white rounded-3xl shadow-xl p-6"
        >
          {/* Step 1: Photos */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-dark-900">Add Photos</h2>
              <p className="text-dark-500 text-sm">
                Upload photos of your item. Our AI will analyze quality and suggest improvements.
              </p>
              <PhotoCapture
                photos={draft.photos || []}
                onPhotosChange={handlePhotosChange}
                maxPhotos={8}
              />
            </div>
          )}

          {/* Step 2: Category */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-dark-900">Select Category</h2>
              <p className="text-dark-500 text-sm">
                AI detected: <span className="font-semibold text-primary-600">Electronics</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['Electronics', 'Vehicles', 'Property', 'Fashion', 'Furniture', 'Jobs'].map(
                  (cat) => (
                    <button
                      key={cat}
                      onClick={() => setDraft((prev) => ({ ...prev, category: cat }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        draft.category === cat
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-dark-200 hover:border-primary-300'
                      }`}
                    >
                      <span className="font-medium">{cat}</span>
                    </button>
                  )
                )}
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-dark-900">Listing Details</h2>
                {!draft.title && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={generateAIContent}
                    disabled={isGenerating}
                    className="flex items-center gap-2 text-primary-600 text-sm font-medium"
                  >
                    {isGenerating ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        <Sparkles className="w-4 h-4" />
                      </motion.div>
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    {isGenerating ? 'Generating...' : 'Auto-Generate'}
                  </motion.button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={draft.title || ''}
                    onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 border border-dark-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="What are you selling?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={draft.description || ''}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, description: e.target.value }))
                    }
                    rows={5}
                    className="w-full px-4 py-3 border border-dark-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    placeholder="Describe your item..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={draft.location || ''}
                    onChange={(e) => setDraft((prev) => ({ ...prev, location: e.target.value }))}
                    className="w-full px-4 py-3 border border-dark-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="City or area"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Pricing */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-dark-900">Set Your Price</h2>
              <p className="text-dark-500 text-sm">
                AI analyzes market data to help you price competitively.
              </p>
              <PriceEstimator
                category={draft.category || 'Electronics'}
                attributes={{ condition: 'Good' }}
                onPriceSelect={handlePriceSelect}
                selectedPrice={draft.price}
              />
            </div>
          )}
        </motion.div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentStep((prev) => Math.max(0, prev - 1))}
            disabled={currentStep === 0}
            className="px-6 py-3 text-dark-500 font-medium disabled:opacity-50"
          >
            Back
          </button>

          {currentStep < listingSteps.length - 1 ? (
            <button
              onClick={() => setCurrentStep((prev) => prev + 1)}
              disabled={!canProceed()}
              className="px-8 py-3 bg-primary-600 text-white rounded-xl font-medium flex items-center gap-2 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={() => alert('Listing published!')}
              disabled={!canProceed()}
              className="px-8 py-3 bg-success text-white rounded-xl font-medium flex items-center gap-2 hover:bg-success-dark disabled:opacity-50 transition-colors"
            >
              <CheckCircle className="w-5 h-5" />
              Publish Listing
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
